/**
 * Insight-casino Explore search matcher.
 *
 * Field matching (case-insensitive) with minimum query lengths:
 * - manufacturer: left-to-right prefix (startsWith), minimum 3 characters
 * - game name / title / gameType: substring, minimum 3 characters
 * - progressive (exact word): isProgressive === true (no short-query match)
 * - mnum (Asset): left-to-right prefix (startsWith), minimum 2 characters
 *   (same floor as floorLocation — asset ids are short numeric strings)
 * - floorLocation: space-normalized prefix (startsWith), minimum 2 characters;
 *   also accepts a compact section+bank prefix so "22A" / "22 A" hit
 *   machines whose display form is "22 A"
 * - exactly 2 characters: bank search against the resolved bank code
 *
 * Section/bank resolution (for headers + bank search):
 * 1. Parse floorLocation LTR — leading letters = section, then a 2-char bank
 *    code (digit+letter or letter+digit, e.g. 6H / 2D / H6), then the rest.
 *    Compacted form (spaces/separators removed) is tried when the raw string
 *    does not parse.
 * 2. If that pattern does not fit (e.g. all-digit floorLocations), fall back
 *    to the machines doc's section / bank fields.
 */

/** Manufacturer / game name-title queries need at least this many characters. */
export const INSIGHT_MIN_NAME_QUERY_LEN = 3;
/** Floor-location prefix queries need at least this many characters (after compact). */
export const INSIGHT_MIN_FLOOR_QUERY_LEN = 2;
/**
 * Asset (mnum) prefix queries need at least this many characters.
 * Chosen to match {@link INSIGHT_MIN_FLOOR_QUERY_LEN} — mnums are short ids
 * and 2 chars is enough to avoid flooding the Asset section on a single keystroke.
 */
export const INSIGHT_MIN_MNUM_QUERY_LEN = 2;

export type InsightSearchableMachine = {
  manufacturer?: string | null;
  /** Prefer when non-blank; else manufacturer (same rule as slotcheckGameName). */
  slotcheckManufacturer?: string | null;
  /** Prefer slotcheckGameName; fall back to gameName / game_name */
  slotcheckGameName?: string | null;
  gameName?: string | null;
  game_name?: string | null;
  /** Prefer slotcheckGameTitle; fall back to gameTitle / title / theme */
  slotcheckGameTitle?: string | null;
  gameTitle?: string | null;
  title?: string | null;
  theme?: string | null;
  floorLocation?: string | number | null;
  /** Machines doc asset id (mnum); Asset section prefix match. */
  mnum?: string | number | null;
  /** machines.slotNumber — DISPLAY id; also searchable in Asset section. */
  slotNumber?: string | number | null;
  gameType?: string | null;
  type?: string | null;
  isProgressive?: boolean | null;
  slot_number?: string | number | null;
  /** Machines doc bank; used when floorLocation does not encode section/bank. */
  bank?: string | null;
  section?: string | null;
};

/** 2-char bank token: digit+letter or letter+digit (6H, 2D, H6). */
const BANK_TOKEN = '([0-9][A-Za-z]|[A-Za-z][0-9])';
/** section (leading letters) + bank + remainder */
const FLOOR_LOCATION_PARSE = new RegExp(`^([A-Za-z]*)${BANK_TOKEN}(.*)$`, 'i');

export type ParsedFloorLocation = {
  section: string;
  bank: string;
  rest: string;
};

export type ResolvedSectionBank = {
  section: string;
  bank: string;
  /** Where section/bank came from. */
  source: 'floorLocation' | 'fields' | 'none';
};

/**
 * Strip spaces and separators for floorLocation prefix compares.
 * Keeps letters/digits only; lowercases. "22 A" / "22-A" / "22a" → "22a".
 *
 * Do NOT use this as the sole needle for game-name matching — compacting
 * "dragon link" → "dragonlink" fails against haystack "dragon link".
 */
export function compactFloorLocationKey(
  value: string | number | null | undefined
): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

/**
 * Normalize text for name/title/gameType compares: lowercase, collapse all
 * Unicode whitespace (incl. NBSP) to a single space, trim.
 * Keeps spaces so multi-word queries like "dragon link" still match.
 */
export function normalizeSearchText(
  value: string | number | null | undefined
): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[\s\u00a0]+/g, ' ')
    .trim();
}

/**
 * Parse floorLocation left-to-right into section + 2-char bank + rest.
 * Returns null when the string has no digit+letter / letter+digit bank token
 * after an optional letter section (typical all-digit floorLocations).
 *
 * Verified examples:
 * - H6H9750 → { section: 'H', bank: '6H', rest: '9750' }
 * - H 6H9750 / H-6H-9750 → same after compact
 * - 6H01    → { section: '',  bank: '6H', rest: '01' }
 * - 2D      → { section: '',  bank: '2D', rest: '' }
 * - 50804   → null (all digits — fall back to doc fields)
 * - 01203   → null (all digits)
 * - E1504   → { section: 'E', bank: '15', rest: '04' }  (JJC: letter + 2-digit bank)
 * - E12     → { section: 'E', bank: '12', rest: '' }
 */
export function parseFloorLocation(
  floorLocation: string | number | null | undefined
): ParsedFloorLocation | null {
  const fl = String(floorLocation ?? '').trim();
  if (!fl) return null;
  const tryParse = (s: string): ParsedFloorLocation | null => {
    const m = FLOOR_LOCATION_PARSE.exec(s);
    if (!m) return null;
    return {
      section: m[1],
      bank: m[2].toUpperCase(),
      rest: m[3] ?? '',
    };
  };
  const tryParseNumericBank = (s: string): ParsedFloorLocation | null => {
    const m = /^([A-Za-z]+)(\d{2})(.*)$/.exec(s);
    if (!m) return null;
    return { section: m[1].toUpperCase(), bank: m[2], rest: m[3] ?? '' };
  };
  const compact = fl.replace(/[^A-Za-z0-9]+/g, '');
  return tryParseNumericBank(compact) ?? tryParse(fl) ?? tryParse(compact);
}

/**
 * Resolve section + bank for headers and bank search.
 * Prefer LTR parse of floorLocation; else machines doc fields.
 */
export function resolveSectionBank(m: InsightSearchableMachine): ResolvedSectionBank {
  const parsed = parseFloorLocation(m.floorLocation);
  if (parsed && (parsed.section || parsed.bank)) {
    return { section: parsed.section, bank: parsed.bank, source: 'floorLocation' };
  }
  const section = String(m.section ?? '').trim();
  const bank = String(m.bank ?? '').trim();
  if (section || bank) {
    return { section, bank, source: 'fields' };
  }
  return { section: '', bank: '', source: 'none' };
}

export function pickGameName(m: InsightSearchableMachine): string {
  const a = String(m.slotcheckGameName ?? '').trim();
  if (a) return a;
  const b = String(m.gameName ?? '').trim();
  if (b) return b;
  return String(m.game_name ?? '').trim();
}

export function pickGameTitle(m: InsightSearchableMachine): string {
  const a = String(m.slotcheckGameTitle ?? '').trim();
  if (a) return a;
  const b = String(m.gameTitle ?? '').trim();
  if (b) return b;
  const c = String(m.title ?? '').trim();
  if (c) return c;
  return String(m.theme ?? '').trim();
}

/** Prefer slotcheckManufacturer; fall back to manufacturer. */
export function pickManufacturer(m: InsightSearchableMachine): string {
  const a = String(m.slotcheckManufacturer ?? '').trim();
  if (a) return a;
  return String(m.manufacturer ?? '').trim();
}

function pickGameType(m: InsightSearchableMachine): string {
  const a = String(m.gameType ?? '').trim();
  if (a) return a;
  return String(m.type ?? '').trim();
}

/**
 * Bank tokens used for 2-char bank search: resolved bank, plus section+bank
 * when that compound is exactly 2 characters (legacy 1+1 section/bank docs).
 */
export function bankTokens(m: InsightSearchableMachine): string[] {
  const { section, bank } = resolveSectionBank(m);
  const tokens: string[] = [];
  if (bank) tokens.push(bank);
  if (section) tokens.push(section);
  const combined = `${section}${bank}`;
  if (combined && combined !== bank && combined !== section) tokens.push(combined);
  return tokens;
}

/** Preferred bank label (resolved), or ''. */
export function pickBankLabel(m: InsightSearchableMachine): string {
  const { bank } = resolveSectionBank(m);
  if (bank) return bank;
  return bankTokens(m)[0] ?? '';
}

/**
 * Bank row / folded-header label, e.g. "Bank I4" / "Bank H6H".
 * Section and bank are joined with no space so rows read "Bank L4 (6)".
 * (Internal keys still use floor / floorLocation — display text only.)
 */
export function floorLocationShortLabel(m: InsightSearchableMachine): string {
  const { section, bank } = resolveSectionBank(m);
  const parts = [section, bank].filter(Boolean).join('');
  return parts ? `Bank ${parts}` : 'Bank';
}

/** Legacy long label; prefer {@link floorLocationShortLabel} under the Bank section. */
export function floorLocationGroupLabel(m: InsightSearchableMachine): string {
  const { section, bank } = resolveSectionBank(m);
  const parts = [section, bank].filter(Boolean).join('');
  return parts ? `Bank Location (${parts})` : 'Bank Location';
}

/**
 * Case/whitespace-insensitive substring match for name-side fields.
 * Uses space-preserving normalization first; also accepts a letters/digits-only
 * compact match on BOTH sides so odd spacing / NBSP in Mongo still hits.
 * Never compacts the needle alone against a spaced haystack.
 */
function includesNameField(haystack: string, needleNormalized: string): boolean {
  if (!needleNormalized) return true;
  const hayNorm = normalizeSearchText(haystack);
  if (hayNorm.includes(needleNormalized)) return true;
  // Compact-both fallback (e.g. Mongo "DRAGON\u00a0LINK" vs typed "dragon link")
  const hayCompact = compactFloorLocationKey(haystack);
  const needleCompact = compactFloorLocationKey(needleNormalized);
  return Boolean(hayCompact) && Boolean(needleCompact) && hayCompact.includes(needleCompact);
}

export function matchesBankQuery(machine: InsightSearchableMachine, query: string): boolean {
  const qLower = String(query ?? '')
    .trim()
    .toLowerCase();
  // Only exact 2-character queries are bank queries.
  if (qLower.length !== 2) return false;
  return bankTokens(machine).some((t) => t.toLowerCase() === qLower);
}

/**
 * floorLocation prefix match (startsWith), case-insensitive.
 * Minimum {@link INSIGHT_MIN_FLOOR_QUERY_LEN} compacted characters.
 * Both sides are compacted (spaces/separators stripped) so "22A", "22 A",
 * and "22a" all match a raw floorLocation like "22 A1234" or "22-A-1234".
 * Also accepts a compact prefix of resolved section+bank (the "22 A" display
 * form) when the raw floorLocation is all-digit / otherwise non-matching.
 */
export function matchesFloorLocationQuery(
  machine: InsightSearchableMachine,
  query: string
): boolean {
  const qNorm = compactFloorLocationKey(query);
  if (!qNorm || qNorm === 'progressive') return false;
  if (qNorm.length < INSIGHT_MIN_FLOOR_QUERY_LEN) return false;

  const flNorm = compactFloorLocationKey(machine.floorLocation);
  if (flNorm && flNorm.startsWith(qNorm)) return true;

  // Derived "section bank" display (e.g. "22 A") — compact to "22a" so typed
  // "22A" / "22 A" still hits when raw floorLocation is numeric-only.
  const { section, bank } = resolveSectionBank(machine);
  const derived = compactFloorLocationKey(`${section}${bank}`);
  if (derived && derived.startsWith(qNorm)) return true;

  return false;
}

/** True when query equals the machine manufacturer (whole value, case-insensitive). */
export function matchesManufacturerExact(
  machine: InsightSearchableMachine,
  query: string
): boolean {
  const q = normalizeSearchText(query);
  if (!q || q.length < INSIGHT_MIN_NAME_QUERY_LEN) return false;
  const manufacturer = normalizeSearchText(pickManufacturer(machine));
  return Boolean(manufacturer) && manufacturer === q;
}

/**
 * Manufacturer left-to-right prefix match (startsWith), case-insensitive.
 * Requires at least {@link INSIGHT_MIN_NAME_QUERY_LEN} characters.
 * Uses space-preserving normalization (not floor compact) so multi-word
 * manufacturers like "Scientific Games" keep working.
 */
export function matchesManufacturerQuery(
  machine: InsightSearchableMachine,
  query: string
): boolean {
  const qNorm = normalizeSearchText(query);
  if (!qNorm) return false;
  if (qNorm === 'progressive') return false;
  if (qNorm.length < INSIGHT_MIN_NAME_QUERY_LEN) return false;
  const manufacturer = normalizeSearchText(pickManufacturer(machine));
  if (!manufacturer) return false;
  return manufacturer.startsWith(qNorm);
}

/** True when the query hits floorLocation (prefix, min 2) and/or bank (exact 2). */
export function matchesLocationQuery(
  machine: InsightSearchableMachine,
  query: string
): boolean {
  return matchesBankQuery(machine, query) || matchesFloorLocationQuery(machine, query);
}

/**
 * Asset (mnum) left-to-right prefix match, case-insensitive.
 * Minimum {@link INSIGHT_MIN_MNUM_QUERY_LEN} characters (2, same as floor).
 */
export function matchesMnumQuery(
  machine: InsightSearchableMachine,
  query: string
): boolean {
  const qNorm = normalizeSearchText(query);
  if (!qNorm || qNorm === 'progressive') return false;
  if (qNorm.length < INSIGHT_MIN_MNUM_QUERY_LEN) return false;
  const mnum = normalizeSearchText(machine.mnum);
  if (!mnum) return false;
  return mnum.startsWith(qNorm);
}

/**
 * machines.slotNumber left-to-right prefix match (Asset / display id search).
 * Same min length as mnum. Never matches floorLocation.
 */
export function matchesSlotNumberQuery(
  machine: InsightSearchableMachine,
  query: string
): boolean {
  const qNorm = normalizeSearchText(query);
  if (!qNorm || qNorm === 'progressive') return false;
  if (qNorm.length < INSIGHT_MIN_MNUM_QUERY_LEN) return false;
  const sn = normalizeSearchText(machine.slotNumber);
  if (!sn) return false;
  return sn.startsWith(qNorm);
}

/** Asset section: match mnum or machines.slotNumber (display id). */
export function matchesAssetQuery(
  machine: InsightSearchableMachine,
  query: string
): boolean {
  return matchesMnumQuery(machine, query) || matchesSlotNumberQuery(machine, query);
}

/**
 * Game-name section fields: game name, game title, gameType, progressive.
 * Name/title/gameType require at least {@link INSIGHT_MIN_NAME_QUERY_LEN} characters.
 * Excludes manufacturer (that goes in the Manufacturer section).
 */
export function matchesGameFieldsQuery(
  machine: InsightSearchableMachine,
  query: string
): boolean {
  const qNorm = normalizeSearchText(query);
  if (!qNorm) return true;

  if (qNorm === 'progressive') {
    return Boolean(machine.isProgressive);
  }

  // Min length on the space-preserving form (not the compacted form) so
  // multi-word queries are not accidentally shortened / rewritten.
  if (qNorm.length < INSIGHT_MIN_NAME_QUERY_LEN) return false;

  const gameName = pickGameName(machine);
  const gameTitle = pickGameTitle(machine);
  const gameType = pickGameType(machine);

  return (
    includesNameField(gameName, qNorm) ||
    includesNameField(gameTitle, qNorm) ||
    includesNameField(gameType, qNorm)
  );
}

/**
 * Name-side fields: manufacturer, game name, game title, gameType, progressive.
 * Used by the overall matcher (any of these counts as a hit).
 */
export function matchesNameSideQuery(
  machine: InsightSearchableMachine,
  query: string
): boolean {
  return matchesManufacturerQuery(machine, query) || matchesGameFieldsQuery(machine, query);
}

/**
 * Returns true when the machine matches the typed query.
 * Empty / whitespace-only query matches everything.
 */
export function matchInsightMachine(
  machine: InsightSearchableMachine,
  query: string
): boolean {
  const q = String(query ?? '').trim();
  if (!q) return true;

  const qLower = q.toLowerCase();

  if (qLower === 'progressive') {
    return Boolean(machine.isProgressive);
  }

  return (
    matchesNameSideQuery(machine, q) ||
    matchesLocationQuery(machine, q) ||
    matchesAssetQuery(machine, q)
  );
}

/** Filter machines by the insight search query. */
export function filterInsightMachinesByQuery<T extends InsightSearchableMachine>(
  machines: T[],
  query: string
): T[] {
  const q = String(query ?? '').trim();
  if (!q) return machines;
  return machines.filter((m) => matchInsightMachine(m, q));
}

function isPresentSlotNumber(s: string | number | null | undefined): s is string | number {
  return s !== null && s !== undefined && String(s).trim() !== '';
}

/** Slot numbers (floorLocation) for machines matching the query. */
export function insightMatchingSlotNumbers(
  machines: InsightSearchableMachine[],
  query: string
): Array<string | number> {
  return filterInsightMachinesByQuery(machines, query)
    .map((m) => m.slot_number ?? m.floorLocation)
    .filter(isPresentSlotNumber);
}
